import React from "react"

import { AUDIO_SENTIMENT_VALUES } from "@/constants/categorical-metric-values"
import {
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"

const SENTIMENT_OPTIONS = AUDIO_SENTIMENT_VALUES.map((value) => ({
  value,
  label: value,
}))

const ROLE_OPTIONS = [
  { key: "agent", label: "AGENT" },
  { key: "persona", label: "PERSONA" },
]

interface CustomAudioSentimentConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const CustomAudioSentimentConfig: React.FC<
  CustomAudioSentimentConfigProps
> = ({ metricRecordData, onChange }) => {
  const selectedSentiments =
    metricRecordData.metric_metadata?.success_sentiments || []
  const role = metricRecordData.metric_metadata?.role || "agent"
  const percentAbove = metricRecordData.metric_metadata?.percent_above

  const handleSentimentsChange = (values: string[]) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        success_sentiments:
          values as MetricRecordData["metric_metadata"]["success_sentiments"],
      },
    })
  }

  const handleRoleChange = (role: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        role: role as "agent" | "persona",
      },
    })
  }

  const handlePercentAboveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        percent_above: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Success Sentiments <span className="text-coval-danger-text">*</span>
          </label>
        </div>
        <div className="flex flex-col gap-2">
          {SENTIMENT_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm"
            >
              <Checkbox
                checked={selectedSentiments.includes(option.value)}
                onCheckedChange={(checked) => {
                  const next = checked
                    ? [...selectedSentiments, option.value]
                    : selectedSentiments.filter((s) => s !== option.value)
                  handleSentimentsChange(next)
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
        <p className="text-xs text-coval-text-muted">
          Select which detected sentiments count as a successful outcome.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Role</label>
        </div>
        <Select value={role} onValueChange={handleRoleChange}>
          <SelectTrigger aria-label="Select role to evaluate">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Which speaker&apos;s segments to classify for sentiment.
        </p>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Percent Above (%) <span className="text-coval-danger-text">*</span>
          </label>
        </div>
        <input
          type="number"
          step="5"
          min="0"
          max="100"
          className={TEXT_INPUT_STYLES}
          value={percentAbove ?? ""}
          onChange={handlePercentAboveChange}
          placeholder="50"
        />
        <p className="text-xs text-coval-text-muted">
          Minimum percentage of segments that must match a success sentiment to
          pass.
        </p>
      </div>
    </div>
  )
}
